import { useState } from 'react';
import { Divider, Group, Menu, Stack } from '@mantine/core';
import classNames from 'classnames';
import IconButton from '@ui/components/IconButton/IconButton';

import './MemberActionsDropdown.scss';

type Props = {
	showDowngradeOption: boolean;
	onDowngradeClicked: () => void;
	onDeleteClicked: () => void;
};

type SelectedOption = 'delete' | 'downgrade';

const SELECTION_FEEDBACK_DELAY_MS = 150;

const MemberActionsDropdown = ( { showDowngradeOption, onDowngradeClicked, onDeleteClicked }: Props ) => {
	const [ menuOpened, setMenuOpened ] = useState( false );
	const [ selected, setSelected ] = useState<SelectedOption>( 'delete' );

	const handleMenuChange = ( opened: boolean ) => {
		if ( opened ) setSelected( 'delete' );
		setMenuOpened( opened );
	};

	const handleDowngradeClick = () => {
		setSelected( 'downgrade' );
		setTimeout( () => {
			setMenuOpened( false );
			onDowngradeClicked();
		}, SELECTION_FEEDBACK_DELAY_MS );
	};

	const handleDeleteClick = () => {
		setMenuOpened( false );
		onDeleteClicked();
	};

	return (
		<Menu
			withinPortal
			position="bottom-end"
			offset={5}
			classNames={{ item: 'MemberActionsDropdown__item' }}
			opened={menuOpened}
			onChange={handleMenuChange}
			closeOnItemClick={false}
		>
			<Menu.Target>
				<IconButton icon="more_vert" size={20} color="var(--color-text-secondary)" aria-label="Member actions" />
			</Menu.Target>
			<Menu.Dropdown className="MemberActionsDropdown__dropdown">
				{showDowngradeOption && (
					<>
						<Menu.Item onClick={handleDowngradeClick}>
							<Group align="flex-start" spacing={6} noWrap>
								<div className={classNames( 'MemberActionsDropdown__radio', { 'MemberActionsDropdown__radio--selected': selected === 'downgrade' } )} />
								<Stack spacing={4}>
									<span className="MemberActionsDropdown__item-title">Downgrade</span>
									<span className="MemberActionsDropdown__item-description">
										Member will lose any<br />
										permissions associated with<br />
										paid seat. Seat will be free to<br />
										assign to another member
									</span>
								</Stack>
							</Group>
						</Menu.Item>
						<Divider color="var(--color-outline-default)" />
					</>
				)}
				<Menu.Item onClick={handleDeleteClick}>
					<Group align="flex-start" spacing={6} noWrap>
						<div className={classNames( 'MemberActionsDropdown__radio', { 'MemberActionsDropdown__radio--selected': selected === 'delete' } )} />
						<Stack spacing={4}>
							<span className="MemberActionsDropdown__item-title">Delete</span>
							<span className="MemberActionsDropdown__item-description">
								Member will be permanently removed from the workspace and all associated projects.
							</span>
						</Stack>
					</Group>
				</Menu.Item>
			</Menu.Dropdown>
		</Menu>
	);
};

export default MemberActionsDropdown;
